import { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Button, LoadingSkeleton, Input, Modal, Select } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  useBankAccounts,
  useBankAccountBalance,
  useCreateBankAccount,
  ACCOUNT_TYPE_LABEL,
  type AccountType,
  type BankAccount,
  type BankAccountInput,
} from './useBankAccounts';

export function BankAccountsPage(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | undefined>(undefined);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: accounts, isLoading, error } = useBankAccounts();
  const { data: balanceData } = useBankAccountBalance(selectedAccountId ?? undefined);
  const createMutation = useCreateBankAccount();

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleImportOfx = useCallback(async (accountId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetch(`/api/v1/finance/accounts/${accountId}/import-ofx`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      showToast('OFX importado com sucesso.', 'success');
    } catch {
      showToast('Erro ao importar OFX.', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const handleCreate = useCallback(
    async (input: BankAccountInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Conta bancária criada com sucesso.', 'success');
        setModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao criar.', 'error');
      }
    },
    [createMutation, showToast],
  );

  /* Saldo consolidado: soma real do campo `saldo` das contas. O prototipo tambem
     traz "API Health" e "Pending Reconciliations", que NAO tem fonte no backend —
     ficaram de fora em vez de exibir numero inventado. */
  const consolidated = (accounts ?? []).reduce((sum, a) => sum + (a.currentBalance ?? 0), 0);
  const activeCount = (accounts ?? []).filter((a) => a.isActive).length;

  return (
    /* ───────────────────────────────────────────────────────────────────────
       Portado de base-html, slug `erpone-gest-o-de-contas-banc-rias`.
       ─────────────────────────────────────────────────────────────────────── */
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Gestão de Contas Bancárias</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Contas bancárias, saldos e integração.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingAccount(undefined);
            setModalOpen(true);
          }}
          className="bg-primary text-on-primary hover:bg-primary-container px-md py-sm rounded font-label-md text-label-md transition-colors shadow-sm flex items-center gap-xs"
        >
          <span className="material-symbols-outlined text-[18px]">add</span>
          Nova Conta
        </button>
      </div>

      {/* Hero: saldo consolidado + contas ativas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-md mb-lg">
        <div className="lg:col-span-2 bg-primary text-on-primary rounded-xl p-lg shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-on-primary/5 rounded-bl-full -mr-8 -mt-8"></div>
          <h3 className="font-body-sm text-body-sm uppercase tracking-wider font-semibold opacity-80 relative z-10">
            Saldo Consolidado
          </h3>
          <span className="font-display-lg text-display-lg font-black tracking-tight block mt-xs relative z-10 tabular-nums">
            {formatCurrency(consolidated)}
          </span>
          <p className="font-body-sm text-body-sm opacity-70 mt-sm relative z-10">
            Soma do saldo das {accounts?.length ?? 0} conta(s) cadastrada(s)
          </p>
        </div>
        <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-lg shadow-sm flex flex-col justify-center">
          <h3 className="font-body-sm text-body-sm text-on-surface-variant uppercase tracking-wider font-semibold">
            Contas Ativas
          </h3>
          <div className="font-headline-md text-headline-md text-on-surface mt-xs tabular-nums">
            {activeCount}
            <span className="font-body-md text-body-md text-on-surface-variant font-normal">
              {' '}/ {accounts?.length ?? 0}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading && (
        <Card variant="outlined" padding="2rem" className="text-centered">
          <LoadingSkeleton />
          <p style={{ color: '#43474e', fontSize: '0.875rem' }}>Carregando contas bancárias...</p>
        </Card>
      )}

      {error && !isLoading && (
        <Card variant="outlined" padding="2rem" className="text-centered">
          <p style={{ color: '#ba1a1a', fontWeight: 600 }}>Erro ao carregar contas bancárias</p>
          <p style={{ fontSize: '0.875rem', color: '#74777f' }}>{(error as Error).message}</p>
        </Card>
      )}

      {!isLoading && !error && (!accounts || accounts.length === 0) && (
        <Card variant="outlined" padding="2rem" className="text-centered muted">
          <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma conta bancária cadastrada.</p>
          <Button variant="outline" size="sm" onClick={() => { setEditingAccount(undefined); setModalOpen(true); }}>Criar primeira conta</Button>
        </Card>
      )}

      {!isLoading && !error && accounts && accounts.length > 0 && (
        <>
          <h2 className="font-headline-sm text-headline-sm text-on-surface mb-sm">Contas Conectadas</h2>
          <div className="flex flex-col gap-md">
          {accounts.map((acc) => (
            <div
              key={acc.id}
              className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm"
            >
              <div className="flex items-center justify-between flex-wrap gap-sm">
                <div>
                  <div className="flex items-center gap-sm">
                    <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">{acc.name}</h3>
                    <span
                      className={`px-2 py-0.5 rounded font-label-sm text-label-sm font-bold tracking-wide uppercase border ${
                        acc.isActive
                          ? 'bg-success-container text-on-success-container border-success-green/30'
                          : 'bg-surface-container text-on-surface-variant border-outline-variant/50'
                      }`}
                    >
                      {acc.isActive ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs mb-0">
                    {/* Conta de caixa nao tem banco nem agencia: mostrar
                        travessao no lugar e mais honesto que uma linha de
                        campos vazios. */}
                    <span className="font-label-md text-label-md">{acc.code}</span>
                    {acc.bankName ? ` · ${acc.bankName}` : ''}
                    {acc.bankAgency ? ` · Ag ${acc.bankAgency}` : ''}
                    {acc.bankAccountNumber ? ` · CC ${acc.bankAccountNumber}` : ''}
                    <span className="ml-sm font-label-sm text-label-sm uppercase">
                      {ACCOUNT_TYPE_LABEL[acc.type] ?? acc.type}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-lg">
                  <div className="text-right">
                    <div className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">
                      Saldo Disponível
                    </div>
                    <div className="font-headline-sm text-headline-sm text-on-surface tabular-nums">
                      {formatCurrency(acc.currentBalance)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedAccountId(acc.id); }}>Saldo</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingAccount(acc); setModalOpen(true); }}>Editar</Button>
                    <label>
                      <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()}>Importar OFX</Button>
                      <input ref={fileInputRef} type="file" accept=".ofx,.ofc" style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          await handleImportOfx(acc.id, file);
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      {/* Balance Modal */}
      <Modal isOpen={!!selectedAccountId} onClose={() => setSelectedAccountId(null)} title="Saldo da Conta">
        <div style={{ padding: '1.5rem', minWidth: '350px' }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem' }}>Saldo da Conta</h2>
          <p style={{ fontSize: '2rem', fontWeight: 700, textAlign: 'center', color: '#002045' }}>
            {balanceData ? formatCurrency(balanceData.currentBalance) : '...'}
          </p>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setSelectedAccountId(null)}>Fechar</Button>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <BankAccountFormModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingAccount(undefined); }}
        account={editingAccount}
        onSubmit={handleCreate}
      />

      {/* Toast */}
      {toastMessage && (
        <div role="alert" style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', padding: '0.75rem 1rem', borderRadius: '8px', background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6', border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`, color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a', fontSize: '0.875rem', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10000, maxWidth: '400px' }}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>
      )}

      <style>{`
        .text-centered { text-align: center; }
        .muted { color: #74777f; }
        .acc-form { display: flex; flex-direction: column; gap: 1rem; }
        .acc-form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
        .acc-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
      `}</style>
    </div>
  );
}

function BankAccountFormModal({ isOpen, onClose, account, onSubmit }: {
  isOpen: boolean;
  onClose: () => void;
  account?: BankAccount;
  onSubmit: (input: BankAccountInput) => Promise<void>;
}) {
  const [code, setCode] = useState(account?.code ?? '');
  const [name, setName] = useState(account?.name ?? '');
  const [type, setType] = useState<AccountType>(account?.type ?? 'CHECKING_ACCOUNT');
  const [bankName, setBankName] = useState(account?.bankName ?? '');
  const [bankAgency, setBankAgency] = useState(account?.bankAgency ?? '');
  const [bankAccountNumber, setBankAccountNumber] = useState(account?.bankAccountNumber ?? '');
  const [initialBalance, setInitialBalance] = useState(account?.initialBalance?.toString() ?? '0');
  const [submitting, setSubmitting] = useState(false);

  /* Conta de caixa e cartao nao tem agencia nem numero de conta; o formulario
     deixa de exigir o que aquele tipo nao tem. */
  const isBankType = type === 'CHECKING_ACCOUNT' || type === 'SAVINGS_ACCOUNT' || type === 'INVESTMENT';
  const valid = !!code && !!name;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        code,
        name,
        type,
        ...(bankName ? { bankName } : {}),
        ...(bankAgency ? { bankAgency } : {}),
        ...(bankAccountNumber ? { bankAccountNumber } : {}),
        ...(initialBalance ? { initialBalance: parseFloat(initialBalance) } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={account ? 'Editar conta' : 'Nova conta financeira'}>
      <form onSubmit={handleSubmit} className="acc-form">
        <div className="acc-form-row">
          <Input label="Código" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ex.: 1.1.01" required />
          <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value as AccountType)}>
            {(Object.keys(ACCOUNT_TYPE_LABEL) as AccountType[]).map((key) => (
              <option key={key} value={key}>
                {ACCOUNT_TYPE_LABEL[key]}
              </option>
            ))}
          </Select>
        </div>
        <Input label="Nome da conta" value={name} onChange={(e) => setName(e.target.value)} required />
        {isBankType && (
          <div className="acc-form-row">
            <Input label="Banco" value={bankName ?? ''} onChange={(e) => setBankName(e.target.value)} />
            <Input label="Agência" value={bankAgency ?? ''} onChange={(e) => setBankAgency(e.target.value)} />
            <Input label="Conta" value={bankAccountNumber ?? ''} onChange={(e) => setBankAccountNumber(e.target.value)} />
          </div>
        )}
        <Input
          label="Saldo inicial (R$)"
          type="number"
          step="0.01"
          value={initialBalance}
          onChange={(e) => setInitialBalance(e.target.value)}
        />
        <div className="acc-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>
            {account ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
